<template>
  <ErrorBoundary :stateError="fetchFilterAttributesError" :hasRetry="hasRetry" @retry="handleRetry">
    <div class="q-mt-md">
      <CPageTitle
        @back="onBack"
        class="report-filters-title"
        backable
        :title="isEditing ? $t('Edit Filter') : $t('Create Filter')"
      />
      <div class="q-mt-md">
        <ErrorBoundary>
          <ReportFilterSettingForm
            @accountChangeCallback="accountChangeCallback"
            :loading="loading"
            :accountOptions="accountOptions"
            :optionsLoading="false"
            :groupAccounts.sync="settingToggleItems['groupAccounts']"
            :accounts.sync="accounts"
            :filterName.sync="filterName"
          />
        </ErrorBoundary>
        <ErrorBoundary>
          <ReportFilterAccountsForm
            :loading="loading"
            :allItemsOption="allItemsOption"
            :optionsLoading="optionsLoading"
            @addCategoryToForm="addCategoryToForm"
            @addANewForm="addANewForm"
            @handleCategoryDelete="handleCategoryDelete"
            @handleRemoveForm="handleRemoveForm"
            :forms.sync="forms"
            :categoryOptions="categoryOptions"
          />
        </ErrorBoundary>

        <ErrorBoundary>
          <ReportFilterTagsForm
            :loading="loading"
            :optionsLoading="optionsLoading"
            @clearFilter="clearFilter"
            @handleTagKeyFiltering="handleTagKeyFiltering"
            @removePairFromEntry="removePairFromEntry"
            @handleRemoveTagForm="handleRemoveTagForm"
            @addNewTagEntry="addNewTagEntry"
            @addTagForm="addTagForm"
            :tagFilters.sync="tagFilters"
            :tagNameOptions="tagNameOptions"
            :tagValueFilter="tagValueFilter"
          />
        </ErrorBoundary>

        <div class="row reverse q-px-lg q-pb-xl q-mt-lg inline full-width">
          <CButton
            :disable="!filterName.trim().length"
            color="primary"
            class="q-mt-lg"
            @click="handleSubmit"
          >
            {{ $t('Save Filter') }}
          </CButton>
          <CButton
            :disable="!filterName.trim().length || reportsLoading"
            :loading="reportsLoading"
            outline
            color="primary"
            class="q-mt-lg q-mx-md"
            @click="handlePreview"
          >
            {{ $t('Preview') }}
          </CButton>
          <CButton outline ref="addBtnRef" color="primary" class="q-mt-lg" @click="onBack">
            {{ $t('Cancel') }}
          </CButton>
        </div>
      </div>
      <div class="q-px-lg">
        <div class="result-horizontal-line" />
        <!-- Amount -->
        <div class="__BillingReport_Header_Content_TotalAmount">
          <CLabelValue :label="$t('Monthly total cost')" align="right" small-label>
            <div class="text-lg">
              <CCurrency
                :value="totalAmount"
                :type="currencyType()"
                :loading="reportsLoading"
                :tooltip="`${currencySymbol(currencyType())}${totalAmount}`"
              />
            </div>
          </CLabelValue>
        </div>
        <BillingReportBody :data="serviceData" :loading="reportsLoading" />
      </div>
    </div>
  </ErrorBoundary>
</template>
<script>
import BillingReportBody from '@/components/BillingReport/BillingReportBody';
import ReportFilterAccountsForm from '@/components/ReportFilters/Form/ReportFilterAccountsForm';
import ReportFilterSettingForm from '@/components/ReportFilters/Form/ReportFilterSettingForm';
import ReportFilterTagsForm from '@/components/ReportFilters/Form/ReportFilterTagsForm';
import CButton from '@/components/uikit/CButton';
import CCurrency from '@/components/uikit/CCurrency';
import CLabelValue from '@/components/uikit/CLabelValue';
import CPageTitle from '@/components/uikit/CPageTitle';
import Loading from '@/components/uikit/Loading';
import currencies from '@/lib/currencies';
import { returnServices } from '@/lib/reports';
import Toggle from '@design-system/components/Toggle.vue';
import Vue from 'vue';
import { mapActions, mapState, mapGetters } from 'vuex';

import { analyzer } from '@/analyzer';

export default {
  components: {
    CPageTitle,
    CButton,
    Toggle,
    Loading,
    ReportFilterSettingForm,
    ReportFilterAccountsForm,
    ReportFilterTagsForm,
    CCurrency,
    CLabelValue,
    BillingReportBody,
  },
  data() {
    return {
      accounts: [],
      loading: false,
      currency: 'USD',
      vendor: this.$store.state.vendor,
      serviceData: [],
      currencyRate: '',
      tagNameFilter: '',
      tagValueFilter: '',
      selectedMonth: '',
      groupByModel: {
        label: 'By Columns',
        value: 'groupByColumns',
      },
      groupByOptions: [
        {
          label: 'By Columns',
          value: 'groupByColumns',
        },
        {
          label: 'By Month',
          value: 'groupByMonth',
        },
      ],
      settingToggleItems: {
        //row 1
        noCache: {
          label: this.$t('Disable Cache'),
          required: true,
          value: false,
        },
        forceLatest: {
          label: this.$t('Force Latest'),
          required: false,
          value: false,
        },
        includeCostCategories: {
          label: this.$t('Include Cost Categories'),
          required: false,
          value: false,
        },
        //row2
        includeTags: {
          label: this.$t('Include Tags'),
          required: false,
          value: false,
        },
        groupAccounts: {
          label: this.$t('Group Accounts'),
          required: false,
          value: false,
        },
      }, // start at 50%
      filterName: '',
      categoryOptions: [
        // {
        //   label: 'Account',
        //   value: 'account',
        //   optionKey: 'resource',
        // },
        {
          label: 'Product Code',
          value: 'productCode',
          optionKey: 'productCode',
        },
        {
          label: 'Service Code',
          value: 'serviceCode',
          optionKey: 'serviceCode',
        },
        {
          label: 'Region',
          value: 'region',
          optionKey: 'region',
        },
        {
          label: 'Zone',
          value: 'zone',
          optionKey: 'zone',
        },
        {
          label: 'Usage Type',
          value: 'usageType',
          optionKey: 'usageType',
        },
        {
          label: 'Instance Type',
          value: 'instanceType',
          optionKey: 'instanceType',
        },
        {
          label: 'Operation',
          value: 'operation',
          optionKey: 'operation',
        },
        {
          label: 'Invoice',
          value: 'invoiceId',
          optionKey: 'invoiceId',
        },
        {
          label: 'Description',
          value: 'description',
          optionKey: 'description',
        },
        {
          label: 'Resource',
          value: 'resourceId',
          optionKey: 'resourceId',
        },
      ],
      forms: [
        {
          categories: {},
          activeCategory: null,
        },
      ],
      tagFilters: [
        {
          data: [{ key: '', value: '', regex: false }],
        },
      ],
    };
  },
  async beforeMount() {
    if (this.isEditing) this.handleEdit();
    await this.handleFetch();
  },
  async created() {
    await this.fetchData();
  },
  computed: {
    ...mapState('reportFilters', {
      reports: state => state.reports,
      reportsLoading: state => state.reportsLoading,
    }),
    ...mapGetters('accounts', ['getAccounts']),
    allData() {
      const allData = this.reports;
      if (allData) {
        return allData;
      }
      return [];
    },
    totalAmount() {
      if (this.serviceData && this.serviceData.length > 0) {
        let total = 0;
        for (const svc of this.serviceData) {
          total += svc.total;
        }
        return total;
      }
      return 0;
    },
    fetchFilterAttributesError() {
      return this.$store.getters['actionsObserver/getActionErrorStatus'](
        'reportFilters/fetchFilterAttributes'
      );
    },
    isEditing() {
      return !!this.$route.params.id;
    },
    monthOptions() {
      return this.$store.getters['monthOptions'](true);
    },
    accountOptions() {
      const accounts = this.getAccounts;
      // const allAccounts = [
      //   this.allAccountsOption,
      //   ...this.$store.getters['reportFilters/getAttributeOptions']('account').filter(
      //     i => i !== 'None'
      //   ),
      // ];
      const allAccounts = [this.allAccountsOption, ...accounts.map(account => account.account_id)];
      return allAccounts;
    },
    tagNameOptions() {
      const tagNameFilter = this.tagNameFilter.trim();
      return this.$store.getters['reportFilters/getTagNameOptions'].filter(i => {
        if (tagNameFilter.length > 0) return i !== 'None' && i.includes(tagNameFilter);
        return true;
      });
    },
    optionsLoading() {
      return this.$store.getters['reportFilters/getOptionsLoading'];
    },
    currencyOptions() {
      return currencies.map(currency => ({
        value: currency,
        label: currency,
      }));
    },
    allAccountsOption() {
      return this.$t('All Accounts');
    },
    allItemsOption() {
      return this.$t('All Items');
    },
    hasRetry() {
      return ![null, false].includes(this.fetchFilterAttributesError);
    },
  },
  watch: {
    //   accounts(newValue, prevValue) {
    //     //handle deselecting all accounts option
    //     if (prevValue.length === this.accountOptions.length) {
    //       if (
    //         newValue.length === this.accountOptions.length - 1 &&
    //         !newValue.includes(this.allAccountsOption)
    //       ) {
    //         this.accounts = [];
    //       }
    //     }
    //   },
    allData() {
      if (!this.allData) {
        return;
      }
      this.serviceData = returnServices(this.allData);
    },
  },

  methods: {
    ...mapActions('reportFilters', {
      fetchReportFilters: 'fetchReportFilters',
      createReportFilter: 'createReportFilter',
      editReportFilter: 'editReportFilter',
      fetchFilterAttributes: 'fetchFilterAttributes',
      fetchReports: 'fetchReports',
    }),
    ...mapActions('actionsObserver', {
      setActionError: 'setActionError',
    }),
    ...mapActions('accounts', {
      fetchAccounts: 'fetchAccounts',
    }),
    async fetchData() {
      await this.fetchAccounts({ force: true });
    },
    currencyType() {
      return this.vendor === 'aws' || this.vendor === 'azure' ? 'usd' : 'jpy';
    },
    currencySymbol(currency) {
      switch (currency) {
        case 'usd':
          return '$';
        case 'jpy':
          return '¥';
        default:
          return '';
      }
    },
    handleFetch() {
      return this.fetchFilterAttributes({ vendor: this.vendor })
        .then(this.handleErrorReset)
        .catch(this.handleFetchError);
    },
    handleFetchError() {
      this.setActionError({
        actionPath: 'reportFilters/fetchReportFilters',
        status: true,
      });
    },
    handleErrorReset() {
      this.setActionError({
        actionPath: 'reportFilters/fetchReportFilters',
        status: false,
      });
    },
    handleRetry() {
      this.handleFetch();
    },
    async handleEdit() {
      this.loading = true;
      const { id } = this.$route.params;
      // await this.handleFetch();
      await this.fetchReportFilters({ vendor: 'aws' });
      const filterData = this.$store.getters['reportFilters/getActiveFilterData']({
        vendor: 'aws',
        filterId: id,
      });
      if (filterData.accountId.length > 0) this.accounts = filterData.accountId.split(',');
      if (filterData.awsOptions.filters.length > 0) {
        this.forms.splice(0, this.forms.length);
        filterData.awsOptions.filters.map((filter, i) => {
          this.forms.push({
            categories: {},
            activeCategory: null,
          });
          for (const [key, value] of Object.entries(filter.andFilters)) {
            const categoryKeyFound = this.categoryOptions.findIndex(
              option => option.optionKey === key
            );
            if (value !== '')
              Vue.set(
                this.forms[i].categories,
                categoryKeyFound > -1 ? this.categoryOptions[categoryKeyFound].value : key,
                value.replace('re:', '').split('|')
              );
          }
        });
      }
      if (filterData.awsOptions.tagFilters.length > 0) {
        this.tagFilters.splice(0, this.tagFilters.length);
        filterData.awsOptions.tagFilters.map((filter, i) => {
          this.tagFilters.push({
            data: [],
          });
          for (const [key, value] of Object.entries(filter.andFilters)) {
            this.tagFilters[i].data.push({ key, value, regex: false });
          }
        });
      }
      this.filterName = filterData.filterName;
      if (filterData.awsOptions.forceLatest)
        this.settingToggleItems.forceLatest.value = filterData.awsOptions.forceLatest;
      if (filterData.awsOptions.groupAccounts)
        this.settingToggleItems.groupAccounts.value = filterData.awsOptions.groupAccounts;
      // if (filterData.awsOptions.groupByMonth)
      //   this.settingToggleItems.groupByMonth.value = filterData.awsOptions.groupByMonth;
      if (filterData.awsOptions.includeCostCategories)
        this.settingToggleItems.includeCostCategories.value =
          filterData.awsOptions.includeCostCategories;
      if (filterData.awsOptions.includeTags)
        this.settingToggleItems.includeTags.value = filterData.awsOptions.includeTags;
      if (filterData.awsOptions.noCache)
        this.settingToggleItems.noCache.value = filterData.awsOptions.noCache;
      if (filterData.awsOptions.toCurrency.split(':')) {
        this.settingToggleItems.currency = filterData.awsOptions.toCurrency.split(':')[0];
        this.settingToggleItems.currencyRate = filterData.awsOptions.toCurrency.split(':')[1];
      }
      this.loading = false;
    },
    handleTagKeyFiltering({ val, entryIndex, formIndex }) {
      this.tagFilters[formIndex].data[entryIndex]['key'] = val;
      this.tagNameFilter = val;
    },
    handleTagValueFiltering(val, entryIndex, formIndex) {
      this.tagFilters[formIndex].data[entryIndex]['value'] = val;
      this.tagValueFilter = val;
    },
    getCategoryOptions(formIndex) {
      return this.categoryOptions.filter(
        item => !Object.keys(this.forms[formIndex].categories).includes(item.value)
      );
    },
    getCategoryOptionKey(category) {
      const found = this.categoryOptions[
        this.categoryOptions.findIndex(item => item.value === category)
      ];
      return found ? found.optionKey : null;
    },
    getPayload() {
      const getFormFilterValues = form => {
        if (!Object.entries(form.categories).length) return null;
        let res = {};
        for (const [key, value] of Object.entries(form.categories)) {
          const optionKey = this.getCategoryOptionKey(key);
          if (optionKey) {
            if (value.includes(this.allItemsOption))
              // tick all items
              res[optionKey] = `re:${this.$store.getters['reportFilters/getAttributeOptions'](
                optionKey
              )
                .filter(item => item !== 'None')
                .join('|')}`;
            // tick only selected items
            else res[optionKey] = `re:${value.join('|')}`;
          } else res[key] = `re:${value.join('|')}`;
        }
        return res;
      };
      const columns = JSON.parse(JSON.stringify(this.forms))
        .map(form => Object.keys(form.categories))
        .flat();
      const settingToggleItems = this.settingToggleItems;
      const formFilters = this.forms
        .map(form => {
          const filters = getFormFilterValues(form);
          if (filters)
            return {
              andFilters: filters,
            };
        })
        .filter(item => !!item);
      return {
        groupId: this.$store.state.companyId,
        // accountId: this.accounts.filter(i => i !== this.allAccountsOption).join(','),
        accountId: this.accounts.includes(this.allAccountsOption)
          ? this.accountOptions.filter(i => i !== this.allAccountsOption).join(',')
          : this.accounts.join(','),
        filterName: this.filterName,
        awsOptions: {
          groupByColumns: [...new Set(columns)].join(','),
          groupByMonth: false,
          groupAccounts: settingToggleItems.groupAccounts.value,
          filters: [...formFilters],
          tagFilters: [...this.getTagValues()],
          includeTags: settingToggleItems.includeTags.value,
          includeCostCategories: settingToggleItems.includeCostCategories.value,
          // toCurrency: `${this.currency}:${this.currencyRate}`,
          toCurrency: '',
          forceLatest: settingToggleItems.forceLatest.value,
          noCache: settingToggleItems.noCache.value,
        },
      };
    },
    submitEdit() {
      this.loading = true;
      const { id } = this.$route.params;
      let payload = {
        vendor: 'aws',
        filterId: id,
        ...this.getPayload(),
      };
      this.editReportFilter(payload)
        .then(() => {
          this.$message({
            message: this.$t('Filter Updated Successfully'),
            type: 'success',
          });
          this.$router.push(`/report_filters/${id}`);
        })
        .catch(() => {
          this.$message.error('Error');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    submitCreate() {
      this.loading = true;
      let payload = {
        vendor: 'aws',
        ...this.getPayload(),
      };
      this.createReportFilter(payload)
        .then(({ data }) => {
          this.$message({
            message: this.$t('Filter Created Successfully'),
            type: 'success',
          });
          this.$router.push(`/report_filters/${data.filterId}`);
        })
        .catch(() => {
          this.$message.error('Error');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleSubmit() {
      if (this.isEditing) this.submitEdit();
      else this.submitCreate();
    },
    async handlePreview() {
      let payload = {
        vendor: this.vendor,
        ...this.getPayload(),
      };
      await this.fetchReports(payload);
      analyzer.track('Action: SaveFilter Preview', {
        logtime2: Number(Date.now()),
      });
    },
    addCategoryToForm({ category, formIndex }) {
      Vue.set(this.forms[formIndex].categories, category, []);
    },
    addANewForm() {
      this.forms.push({
        categories: {},
        activeCategory: null,
      });
    },
    getTagValues() {
      return this.tagFilters
        .map(filter => {
          const entries = filter.data.filter(item => item.key !== '');
          let res = {};
          entries.map(entry => {
            res[entry.key] = entry.value;
          });
          return {
            andFilters: { ...res },
          };
        })
        .filter(item => Object.values(item.andFilters).length > 0);
    },
    handleRemoveForm(formIndex) {
      this.forms.splice(formIndex, 1);
    },
    handleCategoryDelete({ categoryKey, formIndex }) {
      if (this.forms[formIndex].activeCategory === categoryKey)
        this.forms[formIndex].activeCategory = null;
      console.log(categoryKey, formIndex);
      this.forms[formIndex].categories = Object.entries(this.forms[formIndex].categories)
        .filter(([key]) => key !== categoryKey) // remove by filtering
        .reduce((acc, [k, v]) => ((acc[k] = v), acc), {}); // Turn it back to object.
    },
    accountChangeCallback(newData) {
      if (newData.includes(this.allAccountsOption)) {
        // this.accounts = this.accountOptions;
        if (newData[newData.length - 1] === this.allAccountsOption)
          this.accounts = [this.allAccountsOption];
        else this.accounts = newData.filter(i => i !== this.allAccountsOption);
      }
    },
    onBack() {
      this.$router.go(-1);
    },
    handleRemoveTagForm(formIndex) {
      this.tagFilters.splice(formIndex, 1);
    },
    addTagForm() {
      this.tagFilters.push({
        data: [{ key: '', value: '', regex: false }],
      });
      this.tagNameFilter = '';
      this.tagValueFilter = '';
    },
    addNewTagEntry(formIndex) {
      this.tagFilters[formIndex].data.push({ key: '', value: '', regex: false });
      this.tagNameFilter = '';
      this.tagValueFilter = '';
    },
    removePairFromEntry({ formIndex, entryIndex }) {
      this.tagFilters[formIndex].data.splice(entryIndex, 1);
    },
    clearFilter() {
      this.tagNameFilter = '';
      this.tagValueFilter = '';
    },
  },
};
</script>
